import { useState } from "react";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} TeacherReportItem
 * @property {number} id - ID of the teacher
 * @property {string} name - Name of the teacher
 * @property {number} class_count - Number of classes
 * @property {number} student_count - Number of students
 * @property {number} avg_progress - Average progress
 * @property {number} pass_rate - Pass rate
 * @property {number} lessons_assigned - Number of lessons assigned
 */

/**
 * @typedef {object} PerformingTeacher
 * @property {string} name - Name of the teacher
 * @property {number} avg_progress - Average progress
 */

/**
 * @typedef {object} TeacherListPaginated
 * @property {TeacherReportItem[]} data - Array of teacher report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} TeacherReportData
 * @property {number} total_teachers - Total number of teachers
 * @property {number} avg_student_progress - Average student progress
 * @property {PerformingTeacher} highest_performing - Highest performing teacher
 * @property {PerformingTeacher} lowest_performing - Lowest performing teacher
 * @property {TeacherListPaginated} teacher_list - Paginated list of teachers with stats
 */

/**
 * @typedef {object} MetaType
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

export const useGetTeacherReport = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
    /** @type {TeacherReportData | null | undefined} */(undefined)
    );
    const [metaData, setMetaData] = useState(
    /** @type {MetaType | null | undefined} */(undefined)
    );

    /**
     * Fetch teacher report with pagination
     * @async
     * @param {number} [page] - Page number for pagination
     * @param {number} [per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getTeacherReport = async (
        page = 1,
        per_page = ITEMS_PER_PAGE
    ) => {
        setLoading(true);
        setError("");
        try {
            const query = new URLSearchParams();
            query.append("page", page.toString());
            query.append("per_page", per_page.toString());

            const url = `${TENANT_DOMAIN}/school/admin/reports/teachers?${query.toString()}`;
            const response = await axiosInstance.get(url);
            const data = response.data;

            setReportData(data);

            setMetaData({
                current_page: data?.teacher_list?.current_page,
                per_page: data?.teacher_list?.per_page,
                total: data?.teacher_list?.total,
                last_page: data?.teacher_list?.last_page,
            });
        } catch (err) {
            setReportData(null);
            setMetaData(null);
            setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, reportData, getTeacherReport, metaData };
};

